import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Gift } from 'lucide-react';
import { GoogleOAuthButton, AuthDivider, AuthFormField, AuthSubmitButton, AuthError } from '@/components/AuthComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const refCodeFromUrl = (searchParams.get('ref') || '').toUpperCase();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refCodeFromUrl) {
      try { localStorage.setItem('aarova_ref_code', refCodeFromUrl); } catch (_) { /* ignore */ }
    }
  }, [refCodeFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(name, email, password);
    setLoading(false);
    if (result.success) {
      const ref = refCodeFromUrl || (() => { try { return localStorage.getItem('aarova_ref_code') || ''; } catch (_) { return ''; } })();
      if (ref) {
        try {
          await axios.post(`${API_URL}/api/referral/apply-code`, { code: ref }, { withCredentials: true });
          try { localStorage.removeItem('aarova_ref_code'); } catch (_) { /* ignore */ }
        } catch (_) { /* ignore — non-fatal */ }
      }
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <FileText className="w-12 h-12 text-[#FB923C]" strokeWidth={2} />
          </div>
          <h1 className="text-4xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="register-heading">
            Create your account
          </h1>
          <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Start building professional resumes in minutes
          </p>
          {refCodeFromUrl && (
            <div data-testid="referral-banner" className="mt-4 inline-flex items-center gap-2 bg-orange-50 border border-orange-200 px-3 py-1.5 rounded-full text-xs font-bold text-orange-700">
              <Gift className="w-3.5 h-3.5" /> Invited by <code className="font-mono">{refCodeFromUrl}</code> — get 10% off your first subscription
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <AuthError error={error} testId="register-error" />
            <AuthFormField id="name" label="Full Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" testId="register-name-input" required />
            <AuthFormField id="email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" testId="register-email-input" required />
            <AuthFormField id="password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testId="register-password-input" required minLength={6} />
            <AuthSubmitButton loading={loading} loadingText="Creating account..." text="Sign Up" testId="register-submit-btn" />
          </form>

          <AuthDivider />
          <GoogleOAuthButton label="Sign up with Google" />

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link data-testid="register-login-link" to="/login" className="font-bold text-[#FB923C] hover:text-orange-600">Log in</Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link data-testid="register-back-link" to="/" className="text-sm text-slate-600 hover:text-slate-900">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
